import React, { CSSProperties, FC, MouseEvent } from 'react';
import styled from 'styled-components';
import IconDropdown from '../../IconDropdown/IconDropdown';
import { tokens } from '../../../theme';
import { mergeClassNames } from '../../../utils/cssClassNameHelper';
import { IconName, SystemIcon } from '../../Icon/SystemIcon';
import { SystemIconButtonProps } from '../../Icon';
import { Tooltip } from '../../Tooltip/Tooltip';
import MenuList from '../../MenuList/MenuList';
import MenuListItem from '../../MenuList/MenuListItem';
import { useIntlUds } from '../../../translations/UdsIntlProvider';

interface DataTableRowActionsProps {
  actionsLabel?: string;
  dataTest?: string;
  className?: string;
  disabled?: boolean;
  disabledMessage?: string;
  iconName?: SystemIconButtonProps['iconName'];
  /*! enforce internal/mui icon */
  iconSize?: SystemIconButtonProps['size'];
  minMenuWidth?: number;
  onOpen?(): void;
  onClose?(): void;
}

export const DataTableRowActions: FC<React.PropsWithChildren<DataTableRowActionsProps>> = ({
  children,
  actionsLabel,
  dataTest = 'data-table-row-actions',
  className,
  iconName,
  disabled,
  iconSize,
  disabledMessage,
  minMenuWidth,
  onOpen,
  onClose
}) => {
  const { intl } = useIntlUds();
  return (
    <IconDropdown
      disabled={disabled}
      tooltip={actionsLabel ?? intl.DataTable.Dropdown.tooltipText}
      dataTest={dataTest}
      iconName={iconName}
      iconSize={iconSize}
      disabledMessage={disabledMessage}
      onOpen={onOpen}
      onClose={onClose}
    >
      <MenuList
        style={minMenuWidth ? { minWidth: `${minMenuWidth}px` } : undefined}
        ariaLabel="data-table-menu-list"
        className={className}
      >
        {children}
      </MenuList>
    </IconDropdown>
  );
};

interface DataTableRowActionProps {
  disabled?: boolean;
  disabledMessage?: string;
  onClick?: (e: MouseEvent) => void;
  dataTest?: string;
  className?: string;
  style?: CSSProperties;
  iconName?: IconName;
  borderBottom?: boolean;
  borderTop?: boolean;
}

export const DataTableRowAction: FC<React.PropsWithChildren<DataTableRowActionProps>> = ({
  disabled,
  onClick,
  dataTest,
  className,
  style,
  iconName,
  children,
  disabledMessage,
  borderBottom,
  borderTop
}) => {
  const clickHandler = (e: MouseEvent) => {
    if (disabled) {
      e.stopPropagation();
      e.preventDefault();
    } else {
      onClick?.(e);
    }
  };

  // Show Tooltip only if action is disabled.
  const disabledTooltip = !disabled;

  const borderStyles = {
    borderBottom: borderBottom ? `1px solid ${tokens.colorSurfaceVariant}` : 'none',
    borderTop: borderTop ? `1px solid ${tokens.colorSurfaceVariant}` : 'none'
  };

  return (
    <Tooltip title={disabledMessage || ''} disabled={disabledTooltip} tabIndex={-1} placement="top">
      <MenuListItem
        wrapperDataTest="data-table-row-action"
        dataTest={dataTest}
        onClick={clickHandler}
        disabled={disabled}
        className={mergeClassNames(className, {
          disabled
        })}
        style={{ color: tokens.colorOnSurface, ...borderStyles, ...style }}
      >
        {iconName && (
          <StyledDataTableActionIconContainer>
            <SystemIcon name={iconName} />
          </StyledDataTableActionIconContainer>
        )}
        {children}
      </MenuListItem>
    </Tooltip>
  );
};

export const DataTableDropdown = DataTableRowActions;
export const DataTableDropdownAction = DataTableRowAction;

export const StyledDataTableActionIconContainer = styled.div`
  margin-right: ${({ theme }) => theme.spacingSize.ml};
  display: flex;
  align-items: center;
`;
